import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { UiPhoneOption } from "@domun/component-library";
import { countriesSelector, partnerSelector } from "@dashboard/config/redux";
import { ResponseMapType } from "@dashboard/common/types";
import { fetchGetOrder } from "@dashboard/modules/orders/fetchers/orders.fetchers";
import { Order } from "@dashboard/modules/orders/types/order.types";
import { canCreateOrderShipment } from "@dashboard/modules/orders/utils/order-shipment.utils";
import { fetchGetUserAddresses } from "@dashboard/modules/profile/fetchers/user-addresses.fetchers";
import {
  DeliveryCustomer,
  DeliveryProviderQuote,
  DeliverySizeConfig,
  DeliveryTypeConfig,
  DeliveryUser,
  OrderCalculation,
} from "../entities/delivery.entities";
import {
  fetchCalculateAllProviders,
} from "../fetchers/logistic.fetchers";
import { DeliveryMapConfirmResult } from "../entities/delivery.entities";
import { fetchDeliveryConfigurationData } from "../services/remote-config.service";
import { deliveryActions } from "../store/delivery.slice";
import {
  deliveryProvidersLoadingSelector,
  deliveryProvidersSelector,
} from "../store/delivery.selectors";
import { PROFILE_ROUTES } from "@dashboard/modules/profile/profile.routes";
import {
  filterProvidersByDeliveryType,
  getDefaultPhonePrefix,
  getSizesForDeliveryType,
  isExpressDeliveryType,
  pickDefaultDeliveryType,
  pickDefaultProvider,
  pickDefaultSizeForType,
  resolvePartnerCountry,
  resolvePartnerCountryCities,
  resolveCalculationProductsAndTotal,
} from "../utils/delivery-create.utils";
import { useDeliveryToken } from "./use-delivery-token.hook";
import { DELIVERY_ROUTES } from "../delivery.routes";

export const useDeliveryCreate = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderId: orderIdParam } = useParams();
  const token = useDeliveryToken();
  const partner = useSelector(partnerSelector);
  const countries = useSelector(countriesSelector);
  const allProviders = useSelector(deliveryProvidersSelector);
  const providersLoading = useSelector(deliveryProvidersLoadingSelector);
  const userId = partner?.userId;

  const [origin, setOrigin] = useState<DeliveryUser>({});
  const [customer, setCustomer] = useState<Partial<DeliveryCustomer>>({
    city: "",
    address: "",
    lat: 0,
    lng: 0,
    addressDetails: "",
    name: "",
    phone: "",
    prefixPhone: "",
    email: "",
    observations: "",
    postalCode: "",
  });
  const [deliveryTypes, setDeliveryTypes] = useState<DeliveryTypeConfig[]>([]);
  const [allSizes, setAllSizes] = useState<DeliverySizeConfig[]>([]);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState<
    DeliveryTypeConfig | undefined
  >();
  const [deliverySize, setDeliverySize] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [pickupDatesByType, setPickupDatesByType] = useState<
    Record<string, string>
  >({});
  const [calculating, setCalculating] = useState(false);
  const [hasQuoted, setHasQuoted] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [linkedOrder, setLinkedOrder] = useState<Order | null>(null);

  const partnerCountry = useMemo(
    () => resolvePartnerCountry(countries, partner?.userDetail?.country),
    [countries, partner?.userDetail?.country]
  );

  const countryCities = useMemo(
    () => resolvePartnerCountryCities(countries, partner?.userDetail?.country),
    [countries, partner?.userDetail?.country]
  );

  const defaultPhonePrefix = useMemo(
    () =>
      getDefaultPhonePrefix(
        countries,
        partner?.userDetail?.prefixPhone,
        partnerCountry
      ),
    [countries, partner?.userDetail?.prefixPhone, partnerCountry]
  );

  const linkedOrderId = orderIdParam ? Number(orderIdParam) : null;
  const isManualMode = !linkedOrderId;

  const phoneOptions: UiPhoneOption[] = useMemo(
    () =>
      countries.map((c) => ({
        flagUrl: c.flagImage,
        label: c.prefixPhone || "",
        value: c.prefixPhone || "",
      })),
    [countries]
  );

  const sizesForType = useMemo(
    () => getSizesForDeliveryType(allSizes, selectedDeliveryType),
    [allSizes, selectedDeliveryType]
  );

  const selectedDeliverySize = useMemo(
    () => sizesForType.find((s) => s.id === deliverySize),
    [sizesForType, deliverySize]
  );

  const providersForSelectedType = useMemo(
    () =>
      filterProvidersByDeliveryType(
        allProviders,
        selectedDeliveryType?.id
      ),
    [allProviders, selectedDeliveryType?.id]
  );

  const selectedProvider = useMemo(
    () =>
      providersForSelectedType.find((q) => q.provider.id === selectedProviderId) ??
      null,
    [providersForSelectedType, selectedProviderId]
  );

  const isExpressType = isExpressDeliveryType(selectedDeliveryType);
  const showPickupDateField = !!selectedDeliveryType && !isExpressType;

  useEffect(() => {
    if (linkedOrderId || !countries.length) return;
    setCustomer((prev) => {
      if (prev.prefixPhone?.trim()) return prev;
      return { ...prev, prefixPhone: defaultPhonePrefix };
    });
  }, [countries, defaultPhonePrefix, linkedOrderId]);

  useEffect(() => {
    if (!token) return;
    fetchGetUserAddresses(token).then((addresses) => {
      const active = addresses.find((a) => a.active) || addresses[0];
      if (active) {
        setOrigin({
          city: active.city,
          address: active.address,
          lat: active.lat,
          lng: active.lng,
          addressDetails: active.addressDetails,
          postalCode: active.postalCode,
          phone: partner?.userDetail?.phone,
          prefixPhone: partner?.userDetail?.prefixPhone,
          name: partner?.userDetail?.enterpriseName,
        });
      }
    });
    fetchDeliveryConfigurationData().then((data) => {
      const types = data.types || [];
      const sizes = data.sizes || [];
      setDeliveryTypes(types);
      setAllSizes(sizes);
      dispatch(deliveryActions.setDeliveryConfigData(data));

      const defaultType = pickDefaultDeliveryType(types);
      if (defaultType) {
        setSelectedDeliveryType(defaultType);
        setDeliverySize(pickDefaultSizeForType(sizes, defaultType));
      }
    });
  }, [token, partner, dispatch]);

  useEffect(() => {
    if (!token || !linkedOrderId) {
      setLinkedOrder(null);
      return;
    }
    dispatch(deliveryActions.setSourceType("order"));
    dispatch(deliveryActions.setLinkedOrderId(linkedOrderId));
    fetchGetOrder({ id: linkedOrderId, token }).then((order) => {
      if (!order) return;
      if (!canCreateOrderShipment(order)) {
        toast.error(t("delivery.create.validationError"));
        if (userId) navigate(`/${userId}/delivery`);
        return;
      }
      setLinkedOrder(order);
      setAddressSelected(false);
      setLocationConfirmed(false);
      setCustomer({
        name: order.customerName,
        phone: order.customerPhone,
        prefixPhone:
          order.customerPrefixPhone?.trim() || defaultPhonePrefix,
        address: order.customerAddress?.address || "",
        addressDetails: order.customerAddress?.addressDetails || "",
        city: order.customerAddress?.city || "",
        email: order.extraInformation?.email || "",
        observations: "",
        postalCode: "",
        lat: 0,
        lng: 0,
      });
    });
  }, [token, linkedOrderId, dispatch, defaultPhonePrefix, navigate, userId, t]);

  const handlePickupDateChange = useCallback(
    (date: string) => {
      setPickupDate(date);
      if (selectedDeliveryType?.id) {
        setPickupDatesByType((prev) => ({
          ...prev,
          [selectedDeliveryType.id]: date,
        }));
      }
    },
    [selectedDeliveryType?.id]
  );

  const updateCustomer = useCallback(
    (field: keyof DeliveryCustomer, value: string | number) => {
      setCustomer((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handlePhoneChange = useCallback(
    (value: { prefixPhone?: string; phone?: string }) => {
      setCustomer((prev) => ({
        ...prev,
        ...(value.prefixPhone !== undefined && {
          prefixPhone: value.prefixPhone,
        }),
        ...(value.phone !== undefined && { phone: value.phone }),
      }));
    },
    []
  );

  const onDeliveryTypeChange = useCallback(
    (type: DeliveryTypeConfig) => {
      if (selectedDeliveryType?.id && pickupDate) {
        setPickupDatesByType((prev) => ({
          ...prev,
          [selectedDeliveryType.id]: pickupDate,
        }));
      }

      const restoredDate = pickupDatesByType[type.id] ?? "";
      setPickupDate(restoredDate);
      setSelectedDeliveryType(type);
      const nextSize = pickDefaultSizeForType(allSizes, type);
      setDeliverySize(nextSize);
      setSelectedProviderId("");

      if (customer.lat && customer.lng && token) {
        const { products, totalValue } =
          resolveCalculationProductsAndTotal(linkedOrder);
        const calc: OrderCalculation = {
          orderId: linkedOrderId || undefined,
          totalValue,
          user: origin,
          customer: customer as DeliveryCustomer,
          products,
          deliverySize: nextSize || undefined,
          paymentMethod: "online",
          pickupDate:
            !isExpressDeliveryType(type) && restoredDate
              ? restoredDate
              : undefined,
        };
        if (!origin.lat || !origin.lng) return;
        setCalculating(true);
        dispatch(deliveryActions.setProvidersLoading(true));
        fetchCalculateAllProviders(token, calc)
          .then((list) => {
            setHasQuoted(true);
            dispatch(deliveryActions.setProviders(list));
            dispatch(deliveryActions.setCalculation(calc));
            const filtered = filterProvidersByDeliveryType(list, type.id);
            const defaultQuote = pickDefaultProvider(
              filtered,
              type.defaultProviderId
            );
            if (defaultQuote) {
              setSelectedProviderId(defaultQuote.provider.id);
              dispatch(deliveryActions.setSelectedProvider(defaultQuote));
            }
          })
          .catch(() => {
            toast.error(t("delivery.home.loadError"));
          })
          .finally(() => {
            setCalculating(false);
            dispatch(deliveryActions.setProvidersLoading(false));
          });
      }
    },
    [
      allSizes,
      customer,
      origin,
      token,
      linkedOrderId,
      linkedOrder,
      pickupDate,
      pickupDatesByType,
      selectedDeliveryType?.id,
      dispatch,
      t,
    ]
  );

  const resetLocationFlags = useCallback(() => {
    setAddressSelected(false);
    setLocationConfirmed(false);
  }, []);

  const handleCityChange = useCallback((city: string) => {
    setCustomer((prev) => ({
      ...prev,
      city,
      lat: 0,
      lng: 0,
    }));
    resetLocationFlags();
  }, [resetLocationFlags]);

  const handleAddressAutocomplete = useCallback((map: ResponseMapType) => {
    setCustomer((prev) => ({
      ...prev,
      address: map.fullAddress || prev.address || "",
      city: map.locality || prev.city || "",
      lat: map.coords?.lat || 0,
      lng: map.coords?.lng || 0,
    }));
    setAddressSelected(true);
    setLocationConfirmed(false);
    setShowMapModal(true);
  }, []);

  const handleAddressInputChange = useCallback((value: string) => {
    setCustomer((prev) => ({
      ...prev,
      address: value,
      lat: 0,
      lng: 0,
    }));
    resetLocationFlags();
  }, [resetLocationFlags]);

  const handleAddressDetailsChange = useCallback((value: string) => {
    setCustomer((prev) => ({ ...prev, addressDetails: value }));
  }, []);

  const openMapModal = useCallback(() => {
    if (!customer.city?.trim()) {
      toast.error(t("delivery.create.location.selectCityFirst"));
      return;
    }
    setShowMapModal(true);
  }, [customer.city, t]);

  const closeMapModal = useCallback(() => {
    setShowMapModal(false);
  }, []);

  const handleMapConfirm = useCallback((result: DeliveryMapConfirmResult) => {
    setCustomer((prev) => ({
      ...prev,
      address: result.address,
      city: result.city || prev.city,
      lat: result.lat,
      lng: result.lng,
    }));
    setAddressSelected(true);
    setLocationConfirmed(true);
    setShowMapModal(false);
  }, []);

  const buildCalculation = useCallback((): OrderCalculation | null => {
    if (!origin.lat || !origin.lng || !customer.lat || !customer.lng) {
      return null;
    }
    const { products, totalValue } =
      resolveCalculationProductsAndTotal(linkedOrder);
    return {
      orderId: linkedOrderId || undefined,
      totalValue,
      user: origin,
      customer: customer as DeliveryCustomer,
      products,
      provider: selectedProviderId || undefined,
      deliverySize: deliverySize || undefined,
      paymentMethod: "online",
      pickupDate:
        showPickupDateField && pickupDate ? pickupDate : undefined,
      observations: customer.observations?.trim() || undefined,
    };
  }, [
    origin,
    customer,
    selectedProviderId,
    selectedProvider,
    deliverySize,
    pickupDate,
    linkedOrderId,
    linkedOrder,
    showPickupDateField,
  ]);

  const applyProviderSelection = useCallback(
    (quote: DeliveryProviderQuote) => {
      setSelectedProviderId(quote.provider.id);
      dispatch(deliveryActions.setSelectedProvider(quote));
    },
    [dispatch]
  );

  const handleCalculate = useCallback(async () => {
    const calc = buildCalculation();
    if (!calc || !token) {
      toast.error(t("delivery.create.validationError"));
      return;
    }
    setCalculating(true);
    dispatch(deliveryActions.setProvidersLoading(true));
    try {
      const list = await fetchCalculateAllProviders(token, calc);
      setHasQuoted(true);
      dispatch(deliveryActions.setProviders(list));
      dispatch(deliveryActions.setCalculation(calc));
      const filtered = filterProvidersByDeliveryType(
        list,
        selectedDeliveryType?.id
      );
      const defaultQuote = pickDefaultProvider(
        filtered,
        selectedDeliveryType?.defaultProviderId
      );
      if (defaultQuote) {
        applyProviderSelection(defaultQuote);
      } else {
        setSelectedProviderId("");
        dispatch(deliveryActions.setSelectedProvider(null));
      }
    } catch {
      toast.error(t("delivery.create.calculateError"));
    } finally {
      setCalculating(false);
      dispatch(deliveryActions.setProvidersLoading(false));
    }
  }, [
    buildCalculation,
    token,
    dispatch,
    t,
    selectedDeliveryType,
    applyProviderSelection,
  ]);

  const canContinue = useMemo(() => {
    const hasOriginCoords = !!origin.lat && !!origin.lng;
    const hasLocation =
      !!customer.city?.trim() &&
      !!customer.address?.trim() &&
      addressSelected &&
      locationConfirmed &&
      !!customer.lat &&
      !!customer.lng;
    const hasLogistics =
      !!selectedDeliveryType &&
      !!deliverySize &&
      !!selectedProviderId;
    const hasManualCustomer =
      !isManualMode ||
      (!!customer.name?.trim() &&
        !!customer.prefixPhone?.trim() &&
        !!customer.phone?.trim());

    return hasOriginCoords && hasLocation && hasLogistics && hasManualCustomer;
  }, [
    origin,
    customer,
    addressSelected,
    locationConfirmed,
    selectedDeliveryType,
    deliverySize,
    selectedProviderId,
    isManualMode,
  ]);

  const handleContinue = useCallback(() => {
    const calc = buildCalculation();
    if (!calc || !canContinue) {
      toast.error(t("delivery.create.validationError"));
      return;
    }
    dispatch(deliveryActions.setCalculation(calc));
    if (linkedOrderId) {
      navigate(`/${userId}/delivery/${linkedOrderId}/checkout`);
    } else {
      navigate(`/${userId}/delivery/${DELIVERY_ROUTES.MANUAL_CHECKOUT}`);
    }
  }, [
    buildCalculation,
    canContinue,
    dispatch,
    linkedOrderId,
    navigate,
    userId,
    t,
  ]);

  const showProvidersEmptyState =
    hasQuoted &&
    !calculating &&
    !providersLoading &&
    providersForSelectedType.length === 0;

  const handleEditOriginAddress = useCallback(() => {
    if (userId) {
      navigate(`/${userId}/${PROFILE_ROUTES.ROOT}`);
    }
  }, [navigate, userId]);

  const breadcrumbs = [
    {
      label: t("delivery.breadcrumb.delivery"),
      link: `/${userId}/delivery`,
    },
    { label: t("delivery.breadcrumb.create") },
  ];

  return {
    linkedOrderId,
    isManualMode,
    origin,
    customer,
    updateCustomer,
    phoneOptions,
    handlePhoneChange,
    deliveryTypes,
    selectedDeliveryType,
    onDeliveryTypeChange,
    deliverySize,
    setDeliverySize,
    sizesForType,
    selectedDeliverySize,
    selectedProviderId,
    selectedProvider,
    applyProviderSelection,
    providersForSelectedType,
    showProvidersEmptyState,
    handleEditOriginAddress,
    pickupDate,
    setPickupDate: handlePickupDateChange,
    showPickupDateField,
    defaultPhonePrefix,
    hasQuoted,
    calculating,
    handleCalculate,
    handleContinue,
    canContinue,
    breadcrumbs,
    partner,
    partnerCountry,
    countryCities,
    addressSelected,
    locationConfirmed,
    showMapModal,
    handleCityChange,
    handleAddressAutocomplete,
    handleAddressInputChange,
    handleAddressDetailsChange,
    openMapModal,
    closeMapModal,
    handleMapConfirm,
  };
};
